import type { User } from '../context/AuthContext'

export const SAT_TOKEN_KEY = 'sat_token'

const EXP_SKEW_SEC = 60

function base64UrlDecode(input: string): string {
  const pad = input.length % 4 === 0 ? '' : '===='.slice(0, 4 - (input.length % 4))
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/') + pad
  return atob(b64)
}

export function parseAccessTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const json = base64UrlDecode(parts[1])
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

function expSeconds(payload: Record<string, unknown>): number | null {
  const exp = payload.exp
  if (typeof exp === 'number' && Number.isFinite(exp)) return exp
  return null
}

/** True if JWT `exp` is in the future (with small clock skew). */
export function isAccessTokenUnexpired(token: string, skewSeconds: number = EXP_SKEW_SEC): boolean {
  const payload = parseAccessTokenPayload(token)
  if (!payload) return false
  const exp = expSeconds(payload)
  if (exp == null) return false
  const now = Math.floor(Date.now() / 1000)
  return exp > now + skewSeconds
}

/**
 * When `/auth/me` cannot load but the bearer token is still valid (e.g. transient 5xx),
 * derive enough user data from the JWT to keep the app treating the session as signed in.
 * Matches backend `create_stateless_token` claims.
 */
export function userFromAccessToken(jwt: string): User | null {
  if (!isAccessTokenUnexpired(jwt)) return null
  const p = parseAccessTokenPayload(jwt)
  if (!p) return null
  const sub = p.sub
  const id = typeof sub === 'string' ? parseInt(sub, 10) : typeof sub === 'number' ? sub : NaN
  if (!Number.isFinite(id)) return null

  const email = typeof p.email === 'string' ? p.email : ''
  const userName = typeof p.username === 'string' ? p.username : ''
  const role = typeof p.role === 'string' ? p.role : 'student'
  const onboarding =
    typeof p.onboarding_completed === 'boolean' ? p.onboarding_completed : true

  return {
    id,
    email,
    user_name: userName,
    role,
    is_active: p.is_active !== false,
    subscription_status: typeof p.subscription_status === 'string' ? p.subscription_status : 'active',
    plan_code: typeof p.plan_code === 'string' ? p.plan_code : 'trial',
    subscription_end_date:
      p.subscription_end_date == null
        ? null
        : typeof p.subscription_end_date === 'string'
          ? p.subscription_end_date
          : null,
    xp_total: typeof p.xp_total === 'number' ? p.xp_total : 0,
    avatar: typeof p.avatar === 'string' ? p.avatar : undefined,
    onboarding_completed: onboarding,
    target_score_rw: null,
    target_score_math: null,
    sat_date: null,
  }
}

export function readStoredTokenIfUnexpired(): string | null {
  const t = localStorage.getItem(SAT_TOKEN_KEY)
  if (!t) return null
  if (!isAccessTokenUnexpired(t)) {
    localStorage.removeItem(SAT_TOKEN_KEY)
    return null
  }
  return t
}
